import React, { useState, useRef, useEffect } from 'react';
import type { Team } from '../../../../types';
import { useWorkspaceStore, useAvailableTeams } from '../../../../stores/workspaceStore';
import { useAuthStore } from '../../../../stores/authStore';

interface TeamDropdownProps {
  currentTeam: Team | null;
  onManageClick: () => void;
}

export const TeamDropdown: React.FC<TeamDropdownProps> = ({ currentTeam, onManageClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentWorkspace, switchToPersonal, switchToTeam } = useWorkspaceStore();
  const availableTeams = useAvailableTeams();
  const { user } = useAuthStore();

  // 获取当前工作空间对应的团队信息
  const getCurrentTeamInfo = () => {
    if (currentWorkspace.type === 'team' && currentWorkspace.teamId && user?.teamMemberships) {
      const membership = user.teamMemberships.find(
        m => m.team.id === currentWorkspace.teamId
      );
      return membership?.team || null;
    }
    return null;
  };

  const activeTeam = getCurrentTeamInfo() || currentTeam;

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSwitchToPersonal = () => {
    switchToPersonal();
    setIsOpen(false);
  };

  const handleSwitchToTeam = (teamId: string, teamName: string) => {
    switchToTeam(teamId, teamName);
    setIsOpen(false);
  };

  const handleCopyInviteCode = async () => {
    try {
      if (activeTeam) {
        await navigator.clipboard.writeText(activeTeam.inviteCode);
        // TODO: 显示toast提示
        console.log('邀请码已复制');
      }
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 获取当前显示的名称和样式
  const getCurrentDisplayInfo = () => {
    if (currentWorkspace.type === 'personal') {
      return {
        name: '个人空间',
        bgColor: 'bg-blue-100',
        textColor: 'text-blue-800',
        hoverColor: 'hover:bg-blue-200',
        icon: (
          <svg className="hidden sm:block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )
      };
    } else {
      return {
        name: currentWorkspace.teamName || '团队空间',
        bgColor: 'bg-green-100',
        textColor: 'text-green-800',
        hoverColor: 'hover:bg-green-200',
        icon: (
          <svg className="hidden sm:block w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )
      };
    }
  };

  const displayInfo = getCurrentDisplayInfo();

  return (
    <div className="relative min-w-0" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center max-w-full px-2 sm:px-3 py-1 text-sm rounded-full transition-colors duration-200 ${displayInfo.bgColor} ${displayInfo.textColor} ${displayInfo.hoverColor}`}
      >
        {displayInfo.icon}
        <span className="truncate sm:overflow-visible sm:text-clip">{displayInfo.name}</span>
        <svg
          className={`w-4 h-4 ml-1 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" stroke="currentColor" viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-1 w-60 bg-white rounded-md shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            {/* 工作空间选择 */}
            <div className="px-1 py-1 border-b border-gray-100">
              {/* 个人空间选项 */}
              <button
                onClick={handleSwitchToPersonal}
                className={`w-full flex items-center px-2.5 py-1.5 text-sm rounded-md transition-colors ${
                  currentWorkspace.type === 'personal'
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <span className="flex-1 text-left truncate">个人空间</span>
                {currentWorkspace.type === 'personal' && (
                  <svg className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>

              {/* 团队空间选项 */}
              {availableTeams.map(team => (
                <button
                  key={team.id}
                  onClick={() => handleSwitchToTeam(team.id, team.name)}
                  className={`w-full flex items-center px-2.5 py-1.5 text-sm rounded-md transition-colors mt-0.5 ${
                    currentWorkspace.type === 'team' && currentWorkspace.teamId === team.id
                      ? 'bg-green-50 text-green-700' 
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className="flex-1 text-left truncate">{team.name}</span>
                  {currentWorkspace.type === 'team' && currentWorkspace.teamId === team.id && (
                    <svg className="w-3.5 h-3.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            {/* 当前团队详情（仅在团队空间时显示） */}
            {currentWorkspace.type === 'team' && activeTeam && (
              <div className="px-4 py-2 border-b border-gray-100">
                <div className="text-xs text-gray-500 mb-1">当前团队</div>
                <div className="text-sm font-medium text-gray-900">{activeTeam.name}</div>
                {activeTeam.description && (
                  <div className="text-xs text-gray-500 mt-1">{activeTeam.description}</div>
                )}

                {/* 邀请码 */}
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">邀请码</div>
                  <div className="flex items-center justify-between">
                    <code className="text-sm font-mono text-gray-800 bg-gray-100 px-2 py-1 rounded">
                      {activeTeam.inviteCode}
                    </code>
                    <button
                      onClick={handleCopyInviteCode}
                      className="ml-2 text-xs text-blue-500 hover:text-blue-700 hover:underline"
                    >
                      复制
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* 管理团队按钮 */}
            <div className="px-1 py-1">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onManageClick();
                }}
                className="w-full flex items-center px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200"
              >
                <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                管理团队
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};